import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Plus, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API_URL}/api/admin/gastos/gmail/cuentas-extra`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}`, 'Content-Type': 'application/json' });
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('es-ES', { timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'nunca');

const AccountRow = ({ a, onChange }) => {
  const [busy, setBusy] = useState(false);
  const check = async () => {
    setBusy(true);
    const res = await fetch(`${BASE}/${a.id}/check-now`, { method: 'POST', headers: headers() });
    const j = await res.json();
    setBusy(false);
    res.ok ? toast.success(j.message) : toast.error(j.detail || 'Error al revisar');
    onChange();
  };
  const remove = async () => {
    if (!window.confirm(`¿Desconectar ${a.email}? Las facturas ya importadas se mantienen.`)) return;
    await fetch(`${BASE}/${a.id}`, { method: 'DELETE', headers: headers() });
    toast.success('Cuenta desconectada');
    onChange();
  };
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-100" data-testid={`extra-account-${a.id}`}>
      <Mail size={16} className="text-green-700 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate">{a.email}</p>
        <p className="text-xs text-gray-500 truncate">Última revisión: {fmt(a.last_check)}{a.last_check_result ? ` · ${a.last_check_result}` : ''}</p>
      </div>
      <Button size="sm" variant="outline" onClick={check} disabled={busy} data-testid={`extra-account-check-${a.id}`}>
        <RefreshCw size={14} className={busy ? 'animate-spin' : ''} /><span className="ml-1.5 hidden sm:inline">Revisar ahora</span>
      </Button>
      <button onClick={remove} className="p-2 rounded text-gray-400 hover:text-red-600 hover:bg-red-50" title="Desconectar" data-testid={`extra-account-delete-${a.id}`}><Trash2 size={15} /></button>
    </div>
  );
};

export const GmailExtraAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ email: '', app_password: '' });
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    const res = await fetch(BASE, { headers: headers() });
    if (res.ok) setAccounts((await res.json()).accounts);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setSaving(true);
    const res = await fetch(BASE, { method: 'POST', headers: headers(), body: JSON.stringify(form) });
    const j = await res.json();
    setSaving(false);
    if (!res.ok) return toast.error(j.detail || 'No se pudo conectar');
    toast.success(j.message);
    setForm({ email: '', app_password: '' });
    load();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden" data-testid="gmail-extra-accounts">
      <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
        <Mail size={18} className="text-green-700" />
        <h3 className="font-semibold text-gray-900">Otras cuentas automáticas</h3>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-xs text-gray-500">Se revisan automáticamente igual que la cuenta principal. Las facturas pasan por los mismos filtros (proveedores autorizados, solo facturas reales, duplicados).</p>
        {accounts.map(a => <AccountRow key={a.id} a={a} onChange={load} />)}
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
          <Input placeholder="segunda.cuenta@gmail.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="extra-account-email" />
          <Input type="password" placeholder="Contraseña de aplicación" value={form.app_password} onChange={e => setForm(f => ({ ...f, app_password: e.target.value }))} data-testid="extra-account-password" />
          <Button onClick={add} disabled={saving || !form.email || !form.app_password} data-testid="extra-account-add-btn">
            {saving ? <RefreshCw size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}Conectar
          </Button>
        </div>
      </div>
    </div>
  );
};
